import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { getTextWidth } from '~/components/core/ArrayMultiselectFieldWithChipsFormik/utils';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { useAutoSelectSingleOption } from '~/hooks/useAutoSelectSingleOption';

// MUI v5 small Chip non-text overhead (label padding + delete icon + margin).
// Empirically ~22px against `getTextWidth` for our 14px Poppins.
const CHIP_OVERHEAD = 22;
// Tailwind `gap-4` between chips inside our flex container.
const CHIP_GAP = 16;
// Sub-pixel rounding + font-measurement slack so the last-fitting chip
// doesn't end up internally truncating its label.
const CHIP_SLACK = 4;
// Reserved for MUI Autocomplete's hidden input element so chips never push
// it out of the cell.
const INPUT_MIN_WIDTH = 30;
// Reserved for the `+N` pill when there's overflow (~30px wide + margin).
const PLUS_N_RESERVE = 32;

export interface MultiSelectAutoCompleteProps<T> {
  id: string;
  label?: string;
  options: T[];
  value?: T[] | undefined;
  getOptionLabel: (option: T) => string;
  onChange?: (value?: T[] | null) => void;
  disabled?: boolean;
  className?: string;
  // Hard cap on visible chips on top of width-based fit. Leave unset to let
  // the width alone decide.
  limitTags?: number;
  // When the options contain exactly one option, select it automatically. On by default.
  autoSelectSingleOption?: boolean;
}

interface FitChipsProps<T> {
  tags: T[];
  getOptionLabel: (option: T) => string;
  getTagProps: (params: { index: number }) => { key: React.Key } & Record<string, unknown>;
  limitTags?: number;
}

// Fits as many chips as the cell allows and collapses the rest into a
// tooltipped `+N` indicator. Measures the InputBase **parent** rather than
// our own span — our span shares flex space with MUI's hidden <input>, so
// self-measurement only ever sees the currently-occupied chip width, not the
// available cell width. From the parent we subtract the end-adornment (X + ▼
// buttons) and the input's min width to get the area actually available to
// chips.
function FitChips<T>({ tags, getOptionLabel, getTagProps, limitTags }: FitChipsProps<T>): JSX.Element | null {
  const containerRef = React.useRef<HTMLSpanElement>(null);
  const [available, setAvailable] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = containerRef.current;
    const parent = el?.parentElement;
    if (!parent) return undefined;
    const measure = (): void => {
      const endAdornment = parent.querySelector('.MuiAutocomplete-endAdornment') as HTMLElement | null;
      // Guard against pathological mocks where every element reports the
      // same offsetWidth: an endAdornment wider than its own parent would
      // wipe out the available area.
      const endWidth = endAdornment && endAdornment.offsetWidth < parent.offsetWidth ? endAdornment.offsetWidth : 0;
      setAvailable(Math.max(0, parent.offsetWidth - endWidth - INPUT_MIN_WIDTH));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  if (tags.length === 0) return null;

  const font = containerRef.current ? getComputedStyle(containerRef.current).font : '';
  const fitInto = (avail: number): number => {
    let used = 0;
    for (let i = 0; i < tags.length; i++) {
      const w = getTextWidth(getOptionLabel(tags[i]), font);
      const gap = i > 0 ? CHIP_GAP : 0;
      const cost = gap + w + CHIP_OVERHEAD + CHIP_SLACK;
      if (used + cost > avail) return i;
      used += cost;
    }
    return tags.length;
  };
  // Try without a `+N` reserve first; if anything overflows, re-pack against
  // a tighter budget so the indicator has guaranteed room.
  let fit = fitInto(available);
  if (fit < tags.length) {
    fit = fitInto(available - PLUS_N_RESERVE);
  }
  // Always show at least one chip so the user has a label visible.
  if (fit < 1) fit = 1;

  const cap = limitTags !== undefined ? Math.min(limitTags, fit) : fit;
  const visibleItems = tags.slice(0, cap);
  const hiddenCount = tags.length - cap;
  // The visible chip may itself ellipsize in narrow cells, so the tooltip
  // lists everything selected to keep the full selection recoverable.
  const tooltipLabels = tags.map(getOptionLabel).join(', ');

  return (
    // Outer span holds two flex items:
    //   1. inner chip-wrap (flex-shrink:1, overflow-hidden) — shrinks/clips
    //      when the row is too narrow.
    //   2. `+N` indicator (flex-shrink:0) — never shrinks or gets clipped, so
    //      the user always sees the overflow signal even when chips truncate.
    <span ref={containerRef} className="inline-flex min-w-0 max-w-full items-center overflow-hidden">
      <span className="inline-flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        {visibleItems.map((option) => {
          const index = tags.indexOf(option);
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={getOptionLabel(option)} size="small" {...tagProps} />;
        })}
      </span>
      {hiddenCount > 0 && (
        <Tooltip title={tooltipLabels}>
          <span className="ml-1 bg-gray-200 px-1 flex-shrink-0 cursor-default rounded text-xs">+{hiddenCount}</span>
        </Tooltip>
      )}
    </span>
  );
}
FitChips.displayName = 'MultiSelectAutoCompleteFitChips';

const MultiSelectAutoComplete = <T,>({
  id,
  label,
  options,
  value,
  getOptionLabel,
  onChange,
  disabled,
  className,
  limitTags,
  autoSelectSingleOption = true,
}: MultiSelectAutoCompleteProps<T>): JSX.Element => {
  const handleChange = (_event: React.SyntheticEvent, newValue: T[]): void => {
    if (onChange) {
      onChange(newValue);
    }
  };

  useAutoSelectSingleOption<T>({
    enabled: autoSelectSingleOption && !disabled && !!onChange,
    isEmpty: !value?.length,
    options,
    onSelect: (option) => onChange?.([option]),
  });
  return (
    <Autocomplete
      className={className}
      onChange={handleChange}
      size="small"
      value={value}
      multiple
      id={id}
      disabled={disabled}
      options={options}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      renderOption={(props, option, { selected }) => {
        const { ...optionProps } = props;
        return (
          <li {...optionProps}>
            <Checkbox checked={selected} />
            {getOptionLabel(option)}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="standard"
          // MUI sets `flex-wrap: wrap` on InputBase so chips can wrap to multiple
          // lines. We collapse overflow into `+N` instead, so force the row to
          // stay on a single line — otherwise the input falls below the chips
          // and the cell gains a phantom second underline.
          InputProps={{ ...params.InputProps, style: { flexWrap: 'nowrap' } }}
        />
      )}
      renderTags={(tags, getTagProps) => (
        <FitChips<T>
          tags={tags}
          getOptionLabel={getOptionLabel}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getTagProps={getTagProps as any}
          limitTags={limitTags}
        />
      )}
    />
  );
};

export default MultiSelectAutoComplete;
